import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * UI state only — sidebar, theme, locale, active section (docs/00 §4: no server data
 * in Zustand, that is TanStack Query's job).
 *
 * Persisted under `massar.ui`, which is the same key `index.html` reads before first
 * paint to apply the theme and direction. If you rename the key, rename it there too or
 * the app flashes light-LTR on every reload.
 */
export const useUiStore = create()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      /** Mobile/overlay sidebar — never persisted as open, it would trap a fresh visit. */
      sidebarOpen: false,
      theme: 'system',
      locale: 'en',
      /** `null` = the group/all-sections view, available only with global scope. */
      activeSectionId: null,
      /** Groups the user has collapsed in the sidebar, by group key. */
      collapsedNavGroups: [],

      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setTheme: (theme) => set({ theme }),
      setLocale: (locale) => set({ locale }),
      setActiveSection: (activeSectionId) => set({ activeSectionId }),

      toggleNavGroup: (key) =>
        set({
          collapsedNavGroups: get().collapsedNavGroups.includes(key)
            ? get().collapsedNavGroups.filter((k) => k !== key)
            : [...get().collapsedNavGroups, key],
        }),
    }),
    {
      name: 'massar.ui',
      storage: createJSONStorage(() => localStorage),
      partialize: ({ sidebarCollapsed, theme, locale, activeSectionId, collapsedNavGroups }) => ({
        sidebarCollapsed,
        theme,
        locale,
        activeSectionId,
        collapsedNavGroups,
      }),
    },
  ),
);

export default useUiStore;
